import type { ReactNode } from 'react';
import type { TeamSession } from '../auth/auth-gate';
import { t } from '../i18n';
import { Icon } from '../icons';
import { Wordmark } from '../ui';
import { hrefFor, type Route } from './route';

type Section = 'home' | 'queue' | 'pilot' | 'places';

function sectionOf(route: Route): Section | null {
  if (route.name === 'home') return 'home';
  if (route.name === 'pilot') return 'pilot';
  if (route.name === 'places' || route.name === 'place' || route.name === 'menu') return 'places';
  return null;
}

// Wide screens: icon and label in a row. Phones: four equal tabs, icon above the label.
const linkBase =
  'flex min-h-11 items-center gap-2.5 whitespace-nowrap rounded-[10px] px-2.5 text-[14.5px] max-[799px]:min-h-14 max-[799px]:flex-col max-[799px]:justify-center max-[799px]:gap-1 max-[799px]:px-1 max-[799px]:text-[13px]';

// Phones: a straight 3 px bar on the bottom edge, square corners there so it reads as one line.
const currentTab =
  'max-[799px]:relative max-[799px]:rounded-b-none max-[799px]:shadow-none max-[799px]:after:absolute max-[799px]:after:inset-x-0 max-[799px]:after:bottom-0 max-[799px]:after:h-[3px] max-[799px]:after:bg-ink';

function NavLink({
  section,
  href,
  current,
}: {
  section: Exclude<Section, 'queue'>;
  href: string;
  current: boolean;
}) {
  return (
    <a
      href={href}
      aria-current={current ? 'page' : undefined}
      className={
        current
          ? `${linkBase} ${currentTab} bg-canvas font-semibold text-ink shadow-[inset_3px_0_0_var(--antoja-ink)]`
          : `${linkBase} font-medium text-muted hover:text-ink`
      }
    >
      <Icon name={section} />
      {section === 'pilot' ? (
        <>
          <span className="max-[799px]:hidden">{t('nav.pilot')}</span>
          <span className="min-[800px]:hidden">{t('nav.pilotShort')}</span>
        </>
      ) : (
        t(`nav.${section}`)
      )}
    </a>
  );
}

function Account({
  session,
  current,
  onSignOut,
}: {
  session: TeamSession;
  current: boolean;
  onSignOut: () => void;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {/* The account itself is the way to "Tu cuenta" (password). */}
      <a
        href={hrefFor({ name: 'account' })}
        aria-current={current ? 'page' : undefined}
        className={`flex min-h-11 items-center gap-2.5 rounded-[10px] max-[799px]:min-w-11 max-[799px]:pr-2.5 max-[799px]:pl-1.5 min-[800px]:min-w-0 min-[800px]:px-2.5 ${
          current
            ? 'bg-canvas text-ink min-[800px]:shadow-[inset_3px_0_0_var(--antoja-ink)]'
            : 'text-muted hover:text-ink'
        }`}
      >
        <span
          aria-hidden="true"
          className={`flex size-8 flex-none items-center justify-center rounded-full text-[13px] font-semibold ${
            current ? 'bg-ink text-surface' : 'bg-canvas text-ink'
          }`}
        >
          {session.email.charAt(0).toUpperCase()}
        </span>
        <span className="text-[13px] font-medium min-[800px]:hidden">{t('account.short')}</span>
        <span className="grid min-w-0 max-[799px]:hidden">
          <span className="text-sm font-semibold text-ink">{t('account.link')}</span>
          <span className="truncate text-[12.5px] text-muted" title={session.email}>
            {t(`role.${session.role}`)} · {session.email}
          </span>
        </span>
      </a>
      <button
        type="button"
        onClick={onSignOut}
        className="ml-auto min-h-11 rounded-lg px-2 text-[13px] font-medium text-muted hover:text-ink"
      >
        {t('auth.signOut')}
      </button>
    </div>
  );
}

/**
 * Sidebar layout from the brand canvas ("Consola web"): 240 px on wide screens; on
 * phones a top bar with the account and four equal tabs.
 */
export function Shell({
  route,
  session,
  onSignOut,
  children,
}: {
  route: Route;
  session: TeamSession;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const current = sectionOf(route);
  return (
    <div className="grid min-h-dvh bg-canvas text-ink min-[800px]:grid-cols-[240px_minmax(0,1fr)] max-[799px]:grid-rows-[auto_1fr]">
      {/* The white column runs the full height; the nav inside stays in view while scrolling. */}
      <div className="min-w-0 border-line bg-surface max-[799px]:border-b min-[800px]:border-r">
        <nav
          aria-label={t('nav.label')}
          className="flex min-w-0 flex-col gap-7 px-3.5 py-6 max-[799px]:gap-3 max-[799px]:px-4 max-[799px]:pt-3 max-[799px]:pb-0 min-[800px]:sticky min-[800px]:top-0 min-[800px]:h-dvh"
        >
          <div className="flex items-center justify-between gap-3 px-2.5 max-[799px]:px-0">
            <div className="flex items-baseline gap-2">
              <Wordmark />
              <span className="text-[13px] text-muted">{t('app.section')}</span>
            </div>
            <div className="min-[800px]:hidden">
              <Account session={session} current={route.name === 'account'} onSignOut={onSignOut} />
            </div>
          </div>
          <div className="grid gap-0.5 max-[799px]:grid-cols-4 max-[799px]:gap-1">
            <NavLink section="home" href={hrefFor({ name: 'home' })} current={current === 'home'} />
            {/* Not a link yet: muted, with a "Pronto" tag, and skipped by the keyboard. */}
            <span className={`${linkBase} cursor-default font-medium text-muted`}>
              <Icon name="queue" />
              <span className="flex items-center gap-1.5 max-[799px]:gap-1">
                <span className="max-[799px]:hidden">{t('nav.queue')}</span>
                <span className="min-[800px]:hidden">{t('nav.queueShort')}</span>
                <span className="rounded-full border border-line px-1.5 py-px text-[11px] leading-4 font-medium">
                  {t('nav.soon')}
                </span>
              </span>
            </span>
            <NavLink
              section="pilot"
              href={hrefFor({ name: 'pilot' })}
              current={current === 'pilot'}
            />
            <NavLink
              section="places"
              href={hrefFor({ name: 'places' })}
              current={current === 'places'}
            />
          </div>
          <div className="mt-auto border-t border-line pt-3 max-[799px]:hidden">
            <Account session={session} current={route.name === 'account'} onSignOut={onSignOut} />
          </div>
        </nav>
      </div>
      <main className="min-w-0 px-[clamp(16px,3vw,40px)] pt-8 pb-12 max-[799px]:pt-6">
        <div className="mx-auto grid max-w-[1160px] grid-cols-[minmax(0,1fr)] gap-6">
          {children}
        </div>
      </main>
    </div>
  );
}
